import {
  AutocompleteResult,
  Box,
  FormControlSizes,
  Icon,
  IconButton,
  IconName,
  MixpanelEvent,
  SearchSelect,
  Tooltip,
  useDynamicCallback,
  type OrderStrategy,
  type RenderResultFunc,
  type SearchSelectProps,
  type Security,
  type TooltipProps,
} from '@talos/kyoko';
import { OMS_WIDTH } from 'ava/src/styles/dimensions';
import mixpanel from 'mixpanel-browser';
import { useCallback, useMemo, useState } from 'react';
import { useOrderPresets, type IOrderPreset } from '../../providers/OrderPresetsContext';
import { useStrategies } from '../../providers/StrategiesProvider';
import { isStrategySupportedBySecurity } from '../../utils/security';
import { NavigableControlArrow, NavigableControlRow } from '../MarketDataCard/components/NavigableControlRow';
import { SelectedFieldTooltipContent } from '../MarketDataCard/components/SelectedFieldTooltipContent';
import { MARKET_CARD_CONTROL_TOOLTIP_PROPS } from '../MarketDataCard/marketCardControlTooltipProps';

const labelGetter = (value: IOrderPreset) => value.name;
const style = { lineHeight: 1 };
type OrderPresetSearchSelectTooltipProps = Partial<Pick<TooltipProps, 'placement' | 'delay' | 'hideDelay'>>;

export interface IOrderPresetSearchSelectProps extends Omit<SearchSelectProps<IOrderPreset>, 'getLabel' | 'options'> {
  showNavigation?: boolean;
  options: IOrderPreset[];
  onToggleFavorite?: (orderPreset: IOrderPreset) => void;
  tooltipProps?: OrderPresetSearchSelectTooltipProps;
}

// REFACTOR: These components can be refactored into a single FavoritesSearchSelect
// - StrategySelector.tsx
// - SubAccountSearchSelect.tsx
// - OrderPresetSearchSelect.tsx
// - AllocationSelector (maybe)

export function OrderPresetSearchSelect({
  options: _options,
  showNavigation = false,
  onToggleFavorite,
  tooltipProps = MARKET_CARD_CONTROL_TOOLTIP_PROPS,
  ...props
}: IOrderPresetSearchSelectProps) {
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const options = useMemo(
    () => _options.filter(option => (showOnlyFavorites ? option.isFavorite : true)),
    [showOnlyFavorites, _options]
  );
  const favoriteOptions = useMemo(() => options.filter(option => option.isFavorite), [options]);

  const toggleFavorite = useDynamicCallback((orderPreset: IOrderPreset) => {
    onToggleFavorite?.(orderPreset);
    mixpanel.track(MixpanelEvent.FavoriteOrderPreset);
  });

  const renderFavoriteIcon = useCallback(
    (orderPreset: IOrderPreset) => {
      return (
        <Icon
          icon={orderPreset.isFavorite ? IconName.StarSolid : IconName.Star}
          color={orderPreset.isFavorite ? 'yellow.lighten' : 'gray.080'}
          size="fontSizeLarge"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            toggleFavorite(orderPreset);
          }}
          p="spacing12"
          spaceBefore="-spacing12"
        />
      );
    },
    [toggleFavorite]
  );

  const renderResult: RenderResultFunc<IOrderPreset> = useDynamicCallback((searchResults, disabled, searchString) =>
    AutocompleteResult(searchResults, disabled, searchString, renderFavoriteIcon)
  );

  const navigableOptions = favoriteOptions.length ? favoriteOptions : options;
  const currentIndex = navigableOptions.findIndex(preset => preset.id === props.selection?.id);
  const nextIndex = (currentIndex + 1) % navigableOptions.length;
  const prevIndex = (currentIndex - 1 + navigableOptions.length) % navigableOptions.length;

  const searchPrefix = useMemo(
    () => (
      <Box ml="-spacing4" data-testid="toggle-favorites-box">
        <IconButton
          ghost
          onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
          icon={showOnlyFavorites ? IconName.StarSolid : IconName.Star}
          color={showOnlyFavorites ? 'yellow.lighten' : 'gray.080'}
          className="toggle-favorites"
          data-testid="toggle-favorites"
        />
      </Box>
    ),
    [showOnlyFavorites]
  );

  const placeholder = options.length ? 'Select a preset' : 'No preset available';

  const searchSelect = (
    <SearchSelect
      searchPrefix={searchPrefix}
      prefix={
        <Icon icon={IconName.Preferences} color={props.selection ? 'colors.orange.lighten' : 'colors.gray.080'} />
      }
      placeholder={placeholder}
      dropdownSize={FormControlSizes.Default}
      w="100%"
      options={options}
      dropdownWidth={OMS_WIDTH - 32}
      getLabel={labelGetter}
      showClear={true}
      portalize={true}
      disabled={options.length === 0}
      // We do sorting ourselves as we need to consider favorites
      initialSortByLabel={false}
      renderResult={renderResult}
      style={style}
      {...props}
      showTitle={showNavigation ? false : props.showTitle}
    />
  );

  const mainField = showNavigation ? (
    <Tooltip
      {...tooltipProps}
      tooltip={
        <SelectedFieldTooltipContent
          title="Selected Order Preset"
          lines={props.selection?.name ? [props.selection.name] : []}
          emptyMessage={placeholder}
        />
      }
    >
      {searchSelect}
    </Tooltip>
  ) : (
    searchSelect
  );

  return (
    <NavigableControlRow
      left={
        showNavigation && options.length > 0 ? (
          <NavigableControlArrow
            direction="previous"
            size={props.size}
            onClick={() => prevIndex !== currentIndex && props.onChange(navigableOptions.at(prevIndex))}
            className="order-presets-prev"
            tooltip="Previous Favorite Order Preset"
            tooltipProps={tooltipProps}
          />
        ) : undefined
      }
      right={
        showNavigation && options.length > 0 ? (
          <NavigableControlArrow
            direction="next"
            size={props.size}
            onClick={() => nextIndex !== currentIndex && props.onChange(navigableOptions.at(nextIndex))}
            className="order-presets-next"
            tooltip="Next Favorite Order Preset"
            tooltipProps={tooltipProps}
          />
        ) : undefined
      }
    >
      {mainField}
    </NavigableControlRow>
  );
}

export function useOrderPresetSearchSelectOptions({ security }: { security: Security | undefined }) {
  const { orderPresetsList } = useOrderPresets();
  const { strategiesList } = useStrategies();

  const options = useMemo(
    () => filterOrderPresets(orderPresetsList, { security, strategies: strategiesList }),
    [strategiesList, security, orderPresetsList]
  );

  return options;
}

export function filterOrderPresets(
  orderPresets: IOrderPreset[],
  {
    security,
    strategies,
  }: {
    security: Security | undefined;
    strategies: OrderStrategy[];
  }
) {
  const filteredStrategies = strategies
    .filter(strat => isStrategySupportedBySecurity(strat, security))
    .map(strat => strat.Name);

  return orderPresets
    .filter(preset => filteredStrategies.includes(preset.strategy))
    .toSorted((a, b) => {
      if (a.isFavorite && !b.isFavorite) {
        return -1;
      }
      if (!a.isFavorite && b.isFavorite) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });
}
